import { CommerceGrant, RevenueReport, ShopConnection } from '../../../common/hooks/use-commerce-api.hook';
import { InsightSpec, ROUTES, SpecTone, money, msg, pct, pctOf, prioritise } from './insight-spec';

/**
 * The reading at the top of Business → Sales: what sold against the period
 * before, where the money is concentrated, and the sales Ardealy cannot help
 * with because the product is not in the catalogue. Pure.
 */

const n = (v: number | null | undefined): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export type SalesRange = '30d' | '90d' | '365d';
export const SALES_RANGES: SalesRange[] = ['30d', '90d', '365d'];
const DAYS: Record<SalesRange, number> = { '30d': 30, '90d': 90, '365d': 365 };

const iso = (d: Date) => d.toISOString().slice(0, 10);

/**
 * The period and the one just before it, same length — what a delta is
 * measured against. `now` is injectable for tests.
 */
export function periodsFor(range: SalesRange, now: Date = new Date()) {
    const day = 86_400_000;
    const to = new Date(now.getTime());
    const from = new Date(to.getTime() - DAYS[range] * day);
    const prevFrom = new Date(from.getTime() - DAYS[range] * day);
    return { current: { from: iso(from), to: iso(to) }, previous: { from: iso(prevFrom), to: iso(from) } };
}

export const returningShare = (r: Pick<RevenueReport, 'orders' | 'returningOrders'> | null | undefined): number | null =>
    r && r.orders > 0 ? n(r.returningOrders) / r.orders : null;

/** Revenue from products the shop sells that are not in Ardealy. */
export const unlinkedRevenue = (r: RevenueReport): number => (r.byProduct ?? []).filter((p) => !p.inArdealy).reduce((s, p) => s + n(p.revenue), 0);

/** Share of revenue the top `k` products bring, among the products listed. */
export function topShare(r: RevenueReport, k = 3): number | null {
    if (!r.revenue || (r.byProduct ?? []).length < k + 2) return null;
    const top = [...r.byProduct].sort((a, b) => n(b.revenue) - n(a.revenue)).slice(0, k);
    return Math.min(1, top.reduce((s, p) => s + n(p.revenue), 0) / r.revenue);
}

export function revenueInsights(cur: RevenueReport, prev: RevenueReport | null, limit = 6): InsightSpec[] {
    const specs: InsightSpec[] = [];
    const currency = cur.currency ?? undefined;
    const m = (v: number) => (currency ? money(v, currency, 0) : v);

    if (!cur.orders) {
        return [{ id: 'no-orders', tone: 'neutral', title: msg('SALES.INSIGHT.NO_ORDERS') }];
    }

    if (prev && prev.revenue > 0) {
        const change = ((cur.revenue - prev.revenue) / prev.revenue) * 100;
        if (Math.abs(change) >= 1) {
            specs.push({
                id: 'revenue',
                tone: change > 0 ? 'good' : 'bad',
                title: msg(change > 0 ? 'SALES.INSIGHT.REVENUE_UP' : 'SALES.INSIGHT.REVENUE_DOWN', { pct: pctOf(Math.abs(change)) }),
                detail: msg('SALES.INSIGHT.CHANGE_DETAIL', {
                    orders: cur.orders,
                    prevOrders: prev.orders,
                    average: m(cur.averageOrder),
                    prevAverage: m(prev.averageOrder),
                }),
            });
        }
    } else if (prev && prev.revenue === 0) {
        specs.push({ id: 'revenue', tone: 'good', title: msg('SALES.INSIGHT.FIRST_SALES') });
    }

    const unlinked = unlinkedRevenue(cur);
    if (unlinked > 0 && cur.revenue > 0) {
        specs.push({
            id: 'unlinked',
            // Red only when it is a real slice of the business; a percent or two is worth doing, not alarming.
            tone: unlinked / cur.revenue >= 0.15 ? 'bad' : 'watch',
            title: msg('SALES.INSIGHT.UNLINKED_REVENUE', { money: m(unlinked), pct: pct(unlinked / cur.revenue) }),
            detail: msg('SALES.INSIGHT.UNLINKED_REVENUE_DETAIL'),
            action: { label: msg('ACTION.ADD_PRODUCTS'), href: ROUTES.catalog },
        });
    } else if (n(cur.catalogue?.unlinked) > 0) {
        specs.push({
            id: 'unlinked',
            tone: 'watch',
            title: msg('SALES.INSIGHT.UNLINKED_CATALOGUE', { n: cur.catalogue.unlinked, total: cur.catalogue.shopProducts }, cur.catalogue.unlinked),
            action: { label: msg('ACTION.ADD_PRODUCTS'), href: ROUTES.catalog },
        });
    }

    const top = topShare(cur);
    if (top != null && top >= 0.6) {
        specs.push({ id: 'concentrated', tone: 'watch', title: msg('SALES.INSIGHT.CONCENTRATED', { pct: pct(top) }), detail: msg('SALES.INSIGHT.CONCENTRATED_DETAIL') });
    } else {
        const best = [...(cur.byProduct ?? [])].sort((a, b) => n(b.revenue) - n(a.revenue))[0];
        if (best && cur.revenue > 0) {
            specs.push({
                id: 'top-product',
                tone: 'neutral',
                title: msg('SALES.INSIGHT.TOP_PRODUCT', { product: best.title || best.sku || '—', pct: pct(n(best.revenue) / cur.revenue) }),
            });
        }
    }

    const ret = returningShare(cur);
    if (ret != null) {
        if (ret >= 0.3) specs.push({ id: 'returning', tone: 'good', title: msg('SALES.INSIGHT.RETURNING_GOOD', { pct: pct(ret) }) });
        else if (ret < 0.1 && cur.orders >= 20)
            specs.push({ id: 'returning', tone: 'watch', title: msg('SALES.INSIGHT.RETURNING_LOW', { pct: pct(ret) }), detail: msg('SALES.INSIGHT.RETURNING_LOW_DETAIL') });
    }

    const channels = [...(cur.byChannel ?? [])].sort((a, b) => n(b.revenue) - n(a.revenue));
    if (channels.length > 1 && cur.revenue > 0) {
        const lead = channels[0];
        specs.push({
            id: 'channel',
            tone: 'neutral',
            title: msg('SALES.INSIGHT.CHANNEL', {
                channel: { tkey: `SALES.CHANNEL.${String(lead.channel).toUpperCase()}`, fallback: channelName(lead.channel) },
                pct: pct(n(lead.revenue) / cur.revenue),
            }),
        });
    }

    return prioritise(specs, limit, ['revenue']);
}

/** What a shop connection is doing, and what the vendor has chosen not to share. */
export function shopInsights(shop: ShopConnection, platformLabel: string): InsightSpec[] {
    const specs: InsightSpec[] = [];
    const broken = shop.status !== 'active';
    const granted = (g: CommerceGrant) => (shop.grants ?? []).includes(g);

    if (broken) {
        specs.push({ id: 'status', tone: 'bad', title: msg('SALES.SHOP_INSIGHT.BROKEN', { platform: platformLabel }), detail: shop.lastError ? msg('SALES.SHOP_INSIGHT.ERROR', { error: shop.lastError }) : undefined });
    } else {
        const tone: SpecTone = 'good';
        specs.push({
            id: 'status',
            tone,
            title: msg('SALES.SHOP_INSIGHT.LIVE', { platform: platformLabel }),
            detail: shop.lastSyncAt ? msg('SALES.SHOP_INSIGHT.LAST_READ', { date: { date: shop.lastSyncAt } }) : undefined,
            action: granted('sales') ? { label: msg('ACTION.SEE_REVENUE'), href: ROUTES.revenue } : undefined,
        });
    }

    if (shop.backfill?.status === 'running' || shop.backfill?.status === 'pending') {
        specs.push({ id: 'backfill', tone: 'neutral', title: msg('SALES.SHOP_INSIGHT.READING'), detail: msg('SALES.SHOP_INSIGHT.READING_DETAIL', { orders: n(shop.backfill.ordersImported) }) });
    }

    if (!granted('sales')) specs.push({ id: 'no-sales', tone: 'watch', title: msg('SALES.SHOP_INSIGHT.NO_SALES') });
    if (!granted('stock')) specs.push({ id: 'no-stock', tone: 'neutral', title: msg('SALES.SHOP_INSIGHT.NO_STOCK') });
    if (!granted('customers')) specs.push({ id: 'no-customers', tone: 'neutral', title: msg('SALES.SHOP_INSIGHT.NO_CUSTOMERS') });

    return specs;
}

/**
 * A sales channel the translations do not name, said as words: platforms send
 * their own ids ("shopify_draft_order"), and a raw id in a sentence reads as a bug.
 */
export function channelName(channel: unknown): string {
    const words = String(channel ?? '').replace(/[_-]+/g, ' ').trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : '—';
}
